import { create } from 'zustand';

import type { TERM_CATEGORY } from '@/api';
import type { RouteType } from '@/types';

export enum LOCAL_STORAGE_KEY {
  RECENT_SEARCH_LIST = 'recentSearchList',
  SEARCH_LOCATION_LIST = 'searchLocationList',
  BANNER_CLOSED = 'bannerClosed',
  WELLO_UUID = 'welloUuid',
  CARD_BANNER_CLOSED = 'cardBannerClosed',
}

export type ToastType = 'fail' | 'success' | 'warning' | 'info';

export type LayoutType = 'donation' | 'wello' | 'local-market';

export interface ToastOptions {
  toastKey: string;
  type?: ToastType;
  message: string;
  holdTime?: number;
  createdAt: Date;
}

interface WelloWebLinkModalPros {
  descriptions: string;
  link: string;
}

interface Log {
  data: string;
  time: Date;
}

interface GlobalStore {
  termsDrawerType: TERM_CATEGORY | null;
  setTermsDrawerType: (termsDrawerType: TERM_CATEGORY | null) => void;

  welloWebLinkModalProps: WelloWebLinkModalPros | null;
  setWelloWebLinkModalProps: (
    welloWebLinkModal: WelloWebLinkModalPros | null,
  ) => void;

  shareLink: null | string;
  setShareLink: (shareLink: string | null) => void;

  isShowLoginRedirectionConfirmModal: boolean;
  setIsShowLoginRedirectionConfirmModal: (
    isShowLoginDirectionConformModal: boolean,
  ) => void;

  isNaverMapMounted: boolean;
  setIsNaverMapMounted: (isNaverMapMounted: boolean) => void;

  logList: Log[];
  addLog: (log: Log) => void;

  setGlobalNavigation: (globalNavigation: React.ReactNode) => void;
  globalNavigation: React.ReactNode;

  handleLocationServiceConsentDrawer?: (
    isLocationServiceConsent: boolean,
  ) => Promise<void>;
  setHandleLocationServiceConsentDrawer: (
    handleLocationServiceConsentDrawer: GlobalStore['handleLocationServiceConsentDrawer'],
  ) => void;

  currentRouteType?: RouteType;
  setCurrentRouteType: (currentRouteType: RouteType) => void;

  isDonationDesktopHeaderShow: boolean;
  setIsDonationDesktopHeaderShow: (
    isDonationDesktopHeaderShow: boolean,
  ) => void;

  hideBottomNav: boolean;
  setHideBottomNav: (hideBottomNav: boolean) => void;

  //* overrideLayoutType이 설정되면 그 값을, 아니면 기본 계산값 반환
  overrideLayoutType: LayoutType | null;
  setOverrideLayoutType: (layoutType: LayoutType | null) => void;
}

export const useGlobalStore = create<GlobalStore>((set) => {
  return {
    termsDrawerType: null,
    setTermsDrawerType: (termsDrawerType) => set({ termsDrawerType }),

    welloWebLinkModalProps: null,
    setWelloWebLinkModalProps: (welloWebLinkModalProps) =>
      set({ welloWebLinkModalProps }),

    shareLink: null,
    setShareLink: (shareLink) => set({ shareLink }),

    isShowLoginRedirectionConfirmModal: false,
    setIsShowLoginRedirectionConfirmModal: (
      isShowLoginRedirectionConfirmModal,
    ) => set({ isShowLoginRedirectionConfirmModal }),

    isNaverMapMounted: false,
    setIsNaverMapMounted: (isNaverMapMounted) => set({ isNaverMapMounted }),

    logList: [],
    addLog: (log) => set(({ logList }) => ({ logList: [...logList, log] })),

    setGlobalNavigation: (globalNavigation) => set({ globalNavigation }),
    globalNavigation: null,

    // Drawe 띄우고 콜백 역할함
    setHandleLocationServiceConsentDrawer: (
      handleLocationServiceConsentDrawer,
    ) => set({ handleLocationServiceConsentDrawer }),

    setCurrentRouteType: (currentRouteType) => set({ currentRouteType }),

    isDonationDesktopHeaderShow: true,
    setIsDonationDesktopHeaderShow: (isDonationDesktopHeaderShow) =>
      set({ isDonationDesktopHeaderShow }),

    hideBottomNav: false,
    setHideBottomNav: (hideBottomNav) => set({ hideBottomNav }),

    overrideLayoutType: null,
    setOverrideLayoutType: (overrideLayoutType) => set({ overrideLayoutType }),
  };
});
